import { useState, useCallback, useEffect, useRef } from 'react';
import MicrosoftHttpService from '@frontend/AppSettings/services/MicrosoftHttpService';
import { getSharePointConfig } from '@frontend/AppSettings/environmentConfig';

const TASK_DOCS_ROOT = 'TaskDocuments';
const GRAPH_SCOPES = ['https://graph.microsoft.com/Files.ReadWrite.All'] as any[];

const isNotFoundError = (err: unknown): boolean => {
  const e = err as any;
  if (e?.status === 404) return true;
  if (e?.code === 'itemNotFound') return true;
  const msg = String(e?.message || '').toLowerCase();
  return msg.includes('could not be found') || msg.includes('does not exist') || msg.includes('item not found');
};

export interface SharePointFile {
  id: string;
  name: string;
  size: number;
  webUrl: string;
  lastModifiedDateTime: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  '@microsoft.graph.downloadUrl'?: string;
}

export function useTaskDocuments(taskId: string | undefined) {
  const [documents, setDocuments] = useState<SharePointFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const folderIdCache = useRef<Record<string, string>>({});

  const { siteId, driveId } = getSharePointConfig();

  const getService = useCallback(
    () => new MicrosoftHttpService(GRAPH_SCOPES),
    [],
  );

  const resolveTaskFolder = useCallback(
    async (createIfMissing: boolean): Promise<string | null> => {
      if (!taskId) return null;

      const cacheKey = taskId;
      if (folderIdCache.current[cacheKey]) return folderIdCache.current[cacheKey];

      const service = getService();

      try {
        const item = await service.getSharePointItemByPath(
          siteId, driveId, `${TASK_DOCS_ROOT}/${taskId}`,
        );
        const id = item?.id ?? item?.value?.id;
        if (id) {
          folderIdCache.current[cacheKey] = id;
          return id;
        }
      } catch (err) {
        if (!isNotFoundError(err)) throw err;
      }

      if (!createIfMissing) return null;

      let rootFolderId: string | null = null;
      try {
        const rootItem = await service.getSharePointItemByPath(siteId, driveId, TASK_DOCS_ROOT);
        rootFolderId = rootItem?.id ?? rootItem?.value?.id ?? null;
      } catch (err) {
        if (!isNotFoundError(err)) throw err;
      }

      if (!rootFolderId) {
        const created = await service.createSharePointFolder(siteId, driveId, null, TASK_DOCS_ROOT);
        rootFolderId = created?.id ?? null;
      }

      if (!rootFolderId) {
        // Could not resolve or create the TaskDocuments root folder; avoid creating under drive root.
        return null;
      }

      const taskFolder = await service.createSharePointFolder(siteId, driveId, rootFolderId, taskId);
      const folderId = taskFolder?.id;
      if (folderId) folderIdCache.current[cacheKey] = folderId;
      return folderId ?? null;
    },
    [taskId, siteId, driveId, getService],
  );

  const fetchDocuments = useCallback(async () => {
    if (!taskId || !siteId || !driveId) {
      setDocuments([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const folderId = await resolveTaskFolder(false);
      if (!folderId) {
        setDocuments([]);
        return;
      }

      const service = getService();
      const result = await service.getSharePointFolderChildren(siteId, driveId, folderId);
      const items: SharePointFile[] = result?.value ?? result ?? [];
      setDocuments(items.filter((item: SharePointFile) => item.file));
    } catch (err: any) {
      console.error('Error fetching task documents:', err);
      setError(err?.message || 'Failed to load documents');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [taskId, siteId, driveId, resolveTaskFolder, getService]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!taskId) return;

      const folderId = await resolveTaskFolder(true);
      if (!folderId) throw new Error('Could not create task documents folder');

      const service = getService();
      await service.uploadSharePointFile(siteId, driveId, folderId, file.name, file);
      await fetchDocuments();
    },
    [taskId, siteId, driveId, resolveTaskFolder, getService, fetchDocuments],
  );

  const linkFile = useCallback(
    async (sourceItemId: string, fileName: string) => {
      if (!taskId) return;

      const folderId = await resolveTaskFolder(true);
      if (!folderId) throw new Error('Could not create task documents folder');

      const service = getService();
      await service.copySharePointItem(siteId, driveId, sourceItemId, folderId, fileName);

      // Graph copy is async (202) — wait briefly then refresh
      await new Promise((r) => setTimeout(r, 1500));
      await fetchDocuments();
    },
    [taskId, siteId, driveId, resolveTaskFolder, getService, fetchDocuments],
  );

  const removeFile = useCallback(
    async (itemId: string) => {
      const service = getService();
      await service.deleteSharePointItem(siteId, driveId, itemId);
      await fetchDocuments();
    },
    [siteId, driveId, getService, fetchDocuments],
  );

  return { documents, isLoading, error, uploadFile, linkFile, removeFile, refetch: fetchDocuments };
}
